import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import BlockIcon from '@mui/icons-material/Block';
import { useTranslation } from 'react-i18next';
import {
  BLACKLIST_PLATFORM_COLORS,
  BLACKLIST_PLATFORM_LABELS,
  BLACKLIST_PLATFORMS,
  BlacklistEntry,
  BlacklistHit,
  BlacklistKey,
  customerBlacklistService,
} from '../services/customerBlacklistService';
import AddToBlacklistModal, { BLACK, blackButtonSx, formatBlacklistDate } from './AddToBlacklistModal';

export interface BlacklistModalProps {
  open: boolean;
  onClose: () => void;
  /** Paket ekranı şubesi; null = şirket görünümü (Tüm Siparişler) */
  branchId: string | null;
  branchName?: string | null;
  /** Şirket görünümünde "Görünüm" seçimi için şubeler */
  branches?: { id: string; name: string }[];
  /** Açılınca bu kaydı düzenleme penceresinde aç */
  initialEntryId?: number | null;
  /** Ekleme / düzenleme / çıkarma sonrası (üst ekran önbelleğini yenilesin) */
  onChanged?: () => void;
}

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

const formatMoney = (v?: number | null) =>
  v == null ? '—' : `₺${v.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatDateTime = (s?: string | null) => {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const dot = (color?: string) => (
  <Box component="span" sx={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', bgcolor: color || 'text.disabled', flexShrink: 0 }} />
);

const BlacklistModal: React.FC<BlacklistModalProps> = ({
  open, onClose, branchId, branchName, branches = [], initialEntryId, onChanged,
}) => {
  const { t } = useTranslation('salesSystem');
  const k = (key: string, o?: Record<string, unknown>) => t(`orderManagement.blacklist.${key}`, o);

  // Şirket görünümünde seçilen şube ('' = bütün şirket)
  const [viewBranch, setViewBranch] = useState('');
  const isCompanyView = !branchId;
  const effectiveBranch = branchId || viewBranch || null;
  const effectiveBranchName = branchId ? (branchName || '') : (branches.find(b => b.id === viewBranch)?.name || '');

  const [entries, setEntries] = useState<BlacklistEntry[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [platform, setPlatform] = useState<string>('all');
  const [showRemoved, setShowRemoved] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const loadSeq = useRef(0);

  const [menu, setMenu] = useState<{ anchor: HTMLElement; entry: BlacklistEntry } | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [manualOpen, setManualOpen] = useState(false);
  const initialHandled = useRef(false);

  // Geçmiş penceresi
  const [historyFor, setHistoryFor] = useState<BlacklistEntry | null>(null);
  const [hits, setHits] = useState<BlacklistHit[]>([]);
  const [hitsLoading, setHitsLoading] = useState(false);
  const [hitsError, setHitsError] = useState<string | null>(null);

  // Çıkarma onayı
  const [removeFor, setRemoveFor] = useState<BlacklistEntry | null>(null);
  const [removeNote, setRemoveNote] = useState('');
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  // Arama gecikmesi
  useEffect(() => {
    const h = window.setTimeout(() => setSearch(searchText.trim()), 300);
    return () => window.clearTimeout(h);
  }, [searchText]);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    setError(null);
    try {
      const list = await customerBlacklistService.list({ branchId: effectiveBranch, includeRemoved: showRemoved, search });
      if (seq !== loadSeq.current) return;
      setEntries(list);
      setLoaded(true);
    } catch (e: any) {
      if (seq === loadSeq.current) setError(e?.message || k('loadError'));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveBranch, showRemoved, search]);

  useEffect(() => {
    if (!open) return;
    void load();
  }, [open, load]);

  // Kapanınca sıfırla
  useEffect(() => {
    if (open) return;
    initialHandled.current = false;
    setLoaded(false);
    setEntries([]);
    setMenu(null);
    setEditId(null);
    setManualOpen(false);
    setHistoryFor(null);
    setRemoveFor(null);
    setPlatform('all');
    setShowRemoved(false);
    setSearchText('');
    setSearch('');
  }, [open]);

  // İlk yüklemeden sonra istenen kaydı aç
  useEffect(() => {
    if (!open || !loaded || initialHandled.current) return;
    initialHandled.current = true;
    if (initialEntryId) setEditId(initialEntryId);
  }, [open, loaded, initialEntryId]);

  const activeEntries = useMemo(() => entries.filter(e => e.isActive), [entries]);
  const baseRows = showRemoved ? entries.filter(e => !e.isActive) : activeEntries;

  const platformCounts = useMemo(() => {
    const m: Record<string, number> = {};
    baseRows.forEach(e => e.platforms.forEach(p => { m[p] = (m[p] || 0) + 1; }));
    return m;
  }, [baseRows]);

  const visible = useMemo(
    () => (platform === 'all' ? baseRows : baseRows.filter(e => e.platforms.includes(platform))),
    [baseRows, platform],
  );

  const changed = () => {
    void load();
    onChanged?.();
  };

  // ---------------- Geçmiş ----------------

  const openHistory = async (entry: BlacklistEntry) => {
    setHistoryFor(entry);
    setHits([]);
    setHitsError(null);
    setHitsLoading(true);
    try {
      setHits(await customerBlacklistService.hits(entry.id));
    } catch (e: any) {
      setHitsError(e?.message || k('loadError'));
    } finally {
      setHitsLoading(false);
    }
  };

  // ---------------- Çıkar ----------------

  const confirmRemove = async () => {
    if (!removeFor || !removeNote.trim()) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      await customerBlacklistService.remove(removeFor.id, removeNote.trim(), effectiveBranch);
      setRemoveFor(null);
      changed();
    } catch (e: any) {
      setRemoveError(e?.message || k('loadError'));
    } finally {
      setRemoving(false);
    }
  };

  // ---------------- Görünüm parçaları ----------------

  const keyLine = (key: BlacklistKey) => {
    const text = key.display || key.value;
    if (key.type === 'phone') {
      return <Typography key={`${key.type}|${key.value}`} variant="body2" sx={{ fontFamily: MONO, fontSize: 13 }}>{text}</Typography>;
    }
    if (key.type === 'account') {
      return (
        <Box key={`${key.type}|${key.value}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 13 }}>
          {k('keyType.account')}{key.display ? <Box component="span" sx={{ color: 'text.secondary' }}>{key.display}</Box> : null}
        </Box>
      );
    }
    return (
      <Box key={`${key.type}|${key.value}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 13 }}>
        {dot(BLACKLIST_PLATFORM_COLORS[key.type])}
        <span>{k(`keyType.${key.type}`)}</span>
        <Box component="span" sx={{ fontFamily: MONO }}>{text}</Box>
      </Box>
    );
  };

  const customerSub = (e: BlacklistEntry) => {
    if (e.address) return e.address;
    if (e.accountId) {
      const acc = e.keys.find(x => x.type === 'account');
      return `${k('keyType.account')}${acc?.display ? ` · ${acc.display}` : ''}`;
    }
    return null;
  };

  const subtitle = effectiveBranch
    ? k('subtitleBranch', { branch: effectiveBranchName, count: activeEntries.length })
    : k('subtitleCompany', { count: activeEntries.length });

  const chipSx = (on: boolean) => (on ? { bgcolor: BLACK, color: '#fff', '&:hover': { bgcolor: '#000' } } : undefined);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { height: { xs: '100%', md: '85vh' }, display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}>
      {/* Başlık */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap' }}>
        <Box sx={{ width: 32, height: 32, borderRadius: 1, bgcolor: BLACK, color: '#fff', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
          <BlockIcon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 160 }}>
          <Typography variant="h6" fontWeight={800} noWrap>{k('title')}</Typography>
          <Typography variant="caption" color="text.secondary" noWrap display="block">{subtitle}</Typography>
        </Box>
        {isCompanyView && branches.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="bl-branch">{k('viewLabel')}</InputLabel>
            <Select labelId="bl-branch" label={k('viewLabel')} value={viewBranch} onChange={(e) => setViewBranch(String(e.target.value))}>
              <MenuItem value="">{k('wholeCompany')}</MenuItem>
              {branches.map(b => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
            </Select>
          </FormControl>
        )}
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Box>

      {/* Filtreler */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center', px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Chip size="small" label={`${k('all')} ${baseRows.length}`} onClick={() => setPlatform('all')}
          variant={platform === 'all' ? 'filled' : 'outlined'} sx={chipSx(platform === 'all')} />
        {BLACKLIST_PLATFORMS.filter(p => platformCounts[p]).map(p => (
          <Chip key={p} size="small" onClick={() => setPlatform(p)} variant={platform === p ? 'filled' : 'outlined'} sx={chipSx(platform === p)}
            label={(
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                {dot(BLACKLIST_PLATFORM_COLORS[p])}
                {BLACKLIST_PLATFORM_LABELS[p]} {platformCounts[p]}
              </Box>
            )} />
        ))}
        <Chip size="small" label={k('removedFilter')} onClick={() => { setShowRemoved(v => !v); setPlatform('all'); }}
          variant={showRemoved ? 'filled' : 'outlined'}
          sx={showRemoved ? { fontWeight: 700 } : undefined} />
        <Box sx={{ flex: 1 }} />
        {loading && loaded && <CircularProgress size={16} />}
        <TextField size="small" placeholder={k('search')} value={searchText} onChange={(e) => setSearchText(e.target.value)}
          sx={{ width: { xs: '100%', sm: 240 } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        <Button size="small" variant="outlined" color="inherit" onClick={() => setManualOpen(true)}>{k('addManual')}</Button>
      </Box>

      {error && <Alert severity="error" sx={{ borderRadius: 0 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Liste */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {loading && !loaded ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
        ) : visible.length === 0 ? (
          <Typography color="text.secondary" sx={{ p: 4, textAlign: 'center' }}>
            {entries.length === 0 && !search && !showRemoved ? k('empty') : k('noMatchFilter')}
          </Typography>
        ) : (
          <Table size="small" stickyHeader sx={{ minWidth: 960 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>{k('col.customer')}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{k('col.keys')}</TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 220 }}>{k('col.reason')}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{k('col.addedBy')}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{k('col.afterwards')}</TableCell>
                <TableCell sx={{ width: 48 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map(e => {
                const sub = customerSub(e);
                return (
                  <TableRow key={e.id} hover
                    sx={{
                      verticalAlign: 'top',
                      boxShadow: e.isActive ? `inset 3px 0 0 ${BLACK}` : undefined,
                      opacity: e.isActive ? 1 : 0.7,
                    }}>
                    <TableCell>
                      <Typography fontWeight={700} fontSize={14}>{e.displayName || '—'}</Typography>
                      {sub && <Typography variant="caption" color="text.secondary" display="block">{sub}</Typography>}
                      {!e.isActive && (
                        <Box sx={{ mt: 0.5 }}>
                          <Chip size="small" label={k('removedBadge')} sx={{ height: 20 }} />
                          <Typography variant="caption" color="text.secondary" display="block">
                            {[e.removedByName, formatBlacklistDate(e.removedAt)].filter(Boolean).join(' · ')}
                          </Typography>
                          {e.removeNote && <Typography variant="caption" color="text.secondary" display="block" sx={{ fontStyle: 'italic' }}>{e.removeNote}</Typography>}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'grid', gap: 0.25 }}>
                        {e.keys.length ? e.keys.map(keyLine) : '—'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={k(`reason.${e.reason}`)}
                        sx={(theme) => ({ bgcolor: alpha(theme.palette.error.main, 0.12), color: theme.palette.error.main, fontWeight: 700, height: 22 })} />
                      {e.note && (
                        <Typography variant="caption" color="text.secondary"
                          sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', mt: 0.5 }}>
                          {e.note}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{e.createdByName || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {[e.ownerBranchName || k('companyOwner'), formatBlacklistDate(e.createdAt)].filter(Boolean).join(' · ')}
                      </Typography>
                      {e.branchId !== '' && (
                        <Chip size="small" variant="outlined" label={k('onlyBranchBadge', { branch: e.branchName || e.ownerBranchName || '' })} sx={{ height: 20, mt: 0.5 }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {e.hitCount > 0 ? k('afterwardsCount', { count: e.hitCount }) : k('afterwardsNone')}
                      </Typography>
                      {e.lastAction && (
                        <Typography variant="caption" color="text.secondary" display="block">{k(`action.${e.lastAction}`)}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={(ev) => setMenu({ anchor: ev.currentTarget, entry: e })}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Box>

      {/* Satır menüsü */}
      <Menu open={!!menu} anchorEl={menu?.anchor} onClose={() => setMenu(null)}>
        {menu && [
          <MenuItem key="edit" onClick={() => { setEditId(menu.entry.id); setMenu(null); }}>
            {menu.entry.canManage && menu.entry.isActive ? k('menu.edit') : k('menu.open')}
          </MenuItem>,
          <MenuItem key="history" onClick={() => { void openHistory(menu.entry); setMenu(null); }}>{k('menu.history')}</MenuItem>,
          menu.entry.isActive && (menu.entry.canManage ? (
            <MenuItem key="remove" onClick={() => { setRemoveFor(menu.entry); setRemoveNote(''); setRemoveError(null); setMenu(null); }}
              sx={{ color: 'error.main' }}>
              {k('menu.remove')}
            </MenuItem>
          ) : (
            <Tooltip key="remove" title={k('menu.notAllowed', { owner: menu.entry.ownerBranchName || k('companyOwner') })} placement="left">
              <span>
                <MenuItem disabled><ListItemText>{k('menu.remove')}</ListItemText></MenuItem>
              </span>
            </Tooltip>
          )),
        ]}
      </Menu>

      {/* Geçmiş */}
      <Dialog open={!!historyFor} onClose={() => setHistoryFor(null)} maxWidth="md" fullWidth>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={800} noWrap>{k('history.title')}</Typography>
            <Typography variant="caption" color="text.secondary" noWrap display="block">{historyFor?.displayName}</Typography>
          </Box>
          <IconButton onClick={() => setHistoryFor(null)} size="small"><CloseIcon /></IconButton>
        </Box>
        <Box sx={{ overflow: 'auto', maxHeight: '60vh' }}>
          {hitsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : hitsError ? (
            <Alert severity="error" sx={{ m: 2 }}>{hitsError}</Alert>
          ) : hits.length === 0 ? (
            <Typography color="text.secondary" sx={{ p: 4, textAlign: 'center' }}>{k('history.empty')}</Typography>
          ) : (
            <Table size="small" stickyHeader sx={{ minWidth: 640 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>{k('history.order')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{k('history.branch')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">{k('history.amount')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{k('history.decision')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{k('history.by')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {hits.map(h => (
                  <TableRow key={h.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        {dot(BLACKLIST_PLATFORM_COLORS[h.platform])}
                        <span>{BLACKLIST_PLATFORM_LABELS[h.platform] || h.platform}</span>
                        <Box component="span" sx={{ fontFamily: MONO, color: 'text.secondary' }}>#{h.orderRef}</Box>
                      </Box>
                    </TableCell>
                    <TableCell>{h.branchName || '—'}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoney(h.amount)}</TableCell>
                    <TableCell>{k(`action.${h.action}`)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{h.actionByName || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">{formatDateTime(h.actionAt || h.createdAt)}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </Dialog>

      {/* Çıkarma onayı */}
      <Dialog open={!!removeFor} onClose={removing ? undefined : () => setRemoveFor(null)} maxWidth="xs" fullWidth>
        <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
          <Typography variant="h6" fontWeight={800}>{k('remove.title')}</Typography>
        </Box>
        <Box sx={{ px: 2.5, pb: 2, display: 'grid', gap: 1.5 }}>
          <Typography variant="body2">{k('remove.body', { name: removeFor?.displayName || '' })}</Typography>
          <TextField size="small" label={k('remove.noteLabel')} value={removeNote} onChange={(e) => setRemoveNote(e.target.value)}
            required autoFocus multiline minRows={2} inputProps={{ maxLength: 500 }} fullWidth />
          {removeError && <Alert severity="error">{removeError}</Alert>}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: 2.5, py: 1.25, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={() => setRemoveFor(null)} disabled={removing} color="inherit">{k('add.cancel')}</Button>
          <Button variant="contained" onClick={() => void confirmRemove()} disabled={removing || !removeNote.trim()} sx={blackButtonSx}>
            {removing ? <CircularProgress size={18} color="inherit" /> : k('remove.submit')}
          </Button>
        </Box>
      </Dialog>

      {/* Düzenle / elle ekle */}
      <AddToBlacklistModal
        open={editId != null || manualOpen}
        onClose={() => { setEditId(null); setManualOpen(false); }}
        subject={null}
        branchId={effectiveBranch}
        branchName={effectiveBranchName}
        entryId={editId}
        onSaved={() => { setEditId(null); setManualOpen(false); changed(); }}
        onOpenEntry={(id) => { setManualOpen(false); setEditId(id); }}
      />
    </Dialog>
  );
};

export default BlacklistModal;
